import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Container,
  SpaceBetween,
  FormField,
  Select,
  Input,
  Checkbox,
  DatePicker,
} from '@cloudscape-design/components';
import { SCENARIO_OPTIONS } from 'src/fcfixed-allocations/pages/create-fcfixed-allocation-page/constants/scenario-options';
import { FREQUENCY_OPTIONS } from 'src/fcfixed-allocations/pages/create-fcfixed-allocation-page/constants/frequency-options';
import { useRegionOptions } from 'src/fcfixed-allocations/pages/create-fcfixed-allocation-page/utils/region-utils';
import { OptionDefinition } from 'src/common/types/OptionDefinition';
import type { SelectChangeEvent } from 'src/common/types/Events';

const FCFIXED_PREFIX = 'FCFIXED';

// Driver-specific filtered options
const DRIVER_SCENARIO_OPTIONS = SCENARIO_OPTIONS.filter(
  (option) => option.value === 'Actuals',
);

interface FormSectionProps {
  selectedScenario: OptionDefinition | null;
  selectedRegion: OptionDefinition | null;
  selectedFrequency: OptionDefinition | null;
  generatedName: string;
  enableDatasetDate: boolean;
  datasetDate: string;
  onScenarioChange: (event: SelectChangeEvent) => void;
  onRegionChange: (event: SelectChangeEvent) => void;
  onFrequencyChange: (event: SelectChangeEvent) => void;
  onEnableDatasetDateChange: (checked: boolean) => void;
  onDatasetDateChange: (value: string) => void;
}

export const isDateBeforeToday = (date: Date): boolean => {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return date < today;
};

const FormSection: React.FC<FormSectionProps> = ({
  selectedScenario,
  selectedRegion,
  selectedFrequency,
  generatedName,
  enableDatasetDate,
  datasetDate,
  onScenarioChange,
  onRegionChange,
  onFrequencyChange,
  onEnableDatasetDateChange,
  onDatasetDateChange,
}) => {
  const { t } = useTranslation();
  const { data: regionOptions, isLoading: isLoadingRegions } =
    useRegionOptions();

  const taskFrequency = useMemo(() => {
    if (selectedScenario?.value) {
      return FREQUENCY_OPTIONS[selectedScenario.value] || [];
    }
    return [];
  }, [selectedScenario]);

  const showDatasetDate = generatedName
    .toUpperCase()
    .includes(`${FCFIXED_PREFIX}_DAILY`);

  return (
    <Container>
      <SpaceBetween size="m">
        <FormField label={t('scenario')} stretch>
          <Select
            data-testid="scenario-selector"
            selectedOption={selectedScenario}
            onChange={onScenarioChange}
            options={DRIVER_SCENARIO_OPTIONS}
            placeholder={t('fcfixed_scenario_placeholder')}
            ariaRequired
          />
        </FormField>
        <FormField label={t('frequency_value')} stretch>
          <Select
            data-testid="frequency-selector"
            selectedOption={selectedFrequency}
            onChange={onFrequencyChange}
            options={taskFrequency}
            placeholder={t('Choose frequency')}
            disabled={!selectedScenario}
            ariaRequired
          />
        </FormField>
        <FormField label={t('region')} stretch>
          <Select
            data-testid="region-selector"
            selectedOption={selectedRegion}
            onChange={onRegionChange}
            options={regionOptions}
            placeholder={t('Choose region')}
            loadingText={isLoadingRegions ? t('table_loading') : ''}
            statusType={isLoadingRegions ? 'loading' : 'finished'}
            ariaRequired
          />
        </FormField>
        <FormField label={t('Name')} stretch>
          <Input
            data-testid="name"
            value={generatedName}
            disabled
            readOnly
            placeholder={t('name_placeholder')}
          />
        </FormField>
        {showDatasetDate && (
          <>
            <FormField stretch>
              <Checkbox
                data-testid="enable-dataset-date"
                checked={enableDatasetDate}
                onChange={({ detail }) =>
                  onEnableDatasetDateChange(detail.checked)
                }
              >
                Enable Dataset Date
              </Checkbox>
            </FormField>
            {enableDatasetDate && (
              <FormField label="Dataset Date" stretch>
                <DatePicker
                  data-testid="dataset-date"
                  value={datasetDate}
                  onChange={({ detail }) => onDatasetDateChange(detail.value)}
                  placeholder="YYYY-MM-DD"
                  isDateEnabled={isDateBeforeToday}
                />
              </FormField>
            )}
          </>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default FormSection;
